import React, { useEffect, useState } from 'react';
import api from '../../api/axios';
import AdminNavbar from '../../components/AdminNavbar';
import { motion, AnimatePresence } from 'framer-motion';
import { Trash2, Mail, Phone, User, Clock, MessageSquare, Loader2, Inbox } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';

const AdminMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await api.get('/messages');
      setMessages(res.data);
    } catch (e) {
      setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/messages/${id}`);
      setMessages(prev => prev.filter(m => m.id !== id));
    } catch (e) {
      alert('Failed to delete');
    }
  };

  useEffect(() => { fetchMessages(); }, []);

  return (
    <div className="min-h-screen bg-background pb-20">
      <AdminNavbar />
      <div className="container mx-auto px-6 py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-4xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-gold-400 to-gold-200">
              Customer Messages
            </h1>
            <p className="text-gray-400 mt-2">View and manage inquiries from customers</p>
          </div>
          <div className="bg-black/40 px-4 py-2 rounded-full border border-white/10 flex items-center gap-2">
            <Inbox className="w-4 h-4 text-gold-500" />
            <span className="text-white font-medium">{messages.length} Messages</span>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-12 h-12 text-gold-500 animate-spin" />
          </div>
        ) : error && messages.length === 0 ? (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 p-6 rounded-lg text-center">
            {error}
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-20 bg-black/20 rounded-xl border border-white/5">
            <Inbox className="w-16 h-16 text-gray-700 mx-auto mb-4" />
            <p className="text-gray-400 text-xl">No messages found</p>
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            <AnimatePresence>
              {messages.map((m, index) => (
                <motion.div
                  key={m.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-gray-900/40 backdrop-blur-sm border-gray-800 hover:border-gold-500/30 transition-all duration-300 h-full flex flex-col">
                    <CardContent className="p-6 flex-1 flex flex-col">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-xl font-bold text-gold-200 line-clamp-1 pr-4">{m.subject}</h3>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDelete(m.id)}
                          className="text-gray-500 hover:text-red-400 hover:bg-red-500/10 -mt-2 -mr-2"
                        >
                          <Trash2 className="w-5 h-5" />
                        </Button>
                      </div>

                      <div className="relative bg-black/20 rounded-lg p-4 mb-6 border border-white/5 flex-1">
                        <MessageSquare className="absolute top-3 left-3 w-4 h-4 text-gray-600" />
                        <p className="text-gray-300 whitespace-pre-line pl-6 text-sm leading-relaxed">
                          {m.message}
                        </p>
                      </div>

                      <div className="space-y-3 pt-4 border-t border-white/5 mt-auto">
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <User className="w-4 h-4 text-gold-500" />
                          <span className="text-white">{m.name}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <Mail className="w-4 h-4 text-gold-500" />
                          <span className="truncate">{m.email}</span>
                        </div>
                        <div className="flex items-center gap-3 text-sm text-gray-400">
                          <Phone className="w-4 h-4 text-gold-500" />
                          <span>{m.phone}</span>
                        </div>
                        {m.createdAt && (
                          <div className="flex items-center gap-3 text-xs text-gray-500 mt-2 pt-2 border-t border-dashed border-gray-800">
                            <Clock className="w-3 h-3" />
                            <span>{new Date(m.createdAt.seconds * 1000).toLocaleString()}</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AdminMessages;


